import { useAuthStore } from '@/lib/stores/auth';
import { CapabilityIdentifier } from '@/features/billing/capability-identifiers';
import { useFeatureLock } from '@/features/billing/use-feature-lock';
import { useSubscriptionPaused } from '@/features/billing/use-subscription-paused';
import { useRoleCan } from '@/features/access/use-role-access';
import { useCanManageTeam } from '@/features/settings/access/use-can-manage-team';
import type { QuickActionContext } from './quick-actions';

/**
 * Resolves every gate the palette's quick actions need, each from the SAME
 * hook the real control uses — `useOrgRole` for workspace create,
 * `useCanManageTeam` + `useFeatureLock(SETTINGS_TEAM_INVITE)` for invite,
 * `useSubscriptionPaused` for all of them.
 *
 * Nothing here re-derives a predicate. That is the whole point: a role-blocked
 * action is filtered out (like a role-blocked page), because a click on it would
 * be a silent dead end (the target input never mounts, so `focusCommandTarget`
 * polls its 60 frames and gives up with nothing focused).
 *
 * Only mounts while the dialog is open — Radix unmounts `DialogPortal`'s
 * children when closed — so the members/plans reads this adds cost nothing
 * until the user actually presses ⌘K, and both are served from TanStack
 * Query's cache if any page has already loaded them.
 *
 * The workspace id comes from the auth store rather than
 * `useCurrentWorkspaceId`, which is `/settings`-specific (it reads a
 * `?workspaceId=` URL override this dialog can be opened from any route
 * without). Same choice `ApiKeysSection` makes, for the same reason.
 */
export function useQuickActionGates(): QuickActionContext {
  const workspaceId = useAuthStore((s) => s.workspace?.id ?? null);
  const { can, isResolved: roleResolved } = useRoleCan();
  const { canManageTeam, isResolving: teamResolving } = useCanManageTeam(workspaceId);
  const { reason: pausedReason } = useSubscriptionPaused();
  const inviteLock = useFeatureLock(CapabilityIdentifier.SETTINGS_TEAM_INVITE);

  return {
    can,
    canManageTeam,
    teamInviteLocked: inviteLock.isLocked,
    teamInviteLockedReason: inviteLock.unlockingPlanName
      ? `Unlocks on the ${inviteLock.unlockingPlanName} plan.`
      : inviteLock.preview,
    pausedReason,
    // `false` on any of these is indistinguishable from a resolved editor —
    // fine for hiding a CTA, wrong for a row that TELLS the user they lack
    // permission. So the rows stay enabled-looking until every gate lands and
    // `QuickActions` simply no-ops a click in the meantime, exactly as
    // `CreateApiKeyItem` already does with `gate.isResolving`.
    isResolving: !roleResolved || teamResolving || inviteLock.isResolving,
  };
}
